import { NavLink, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import type { Capability } from '@/lib/auth'
import { Wordmark } from './Wordmark'

interface NavEntry {
  to: string
  label: string
  icon: ReactNode
  capability?: Capability
  /** Shown nested under the parent while that section is open. */
  children?: { to: string; label: string }[]
}

const NAV: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: <DashboardIcon /> },
  { to: '/gantt', label: 'Gantt', icon: <GanttIcon />, capability: 'schedule.view' },
  {
    to: '/settings',
    label: 'Settings',
    icon: <SettingsIcon />,
    capability: 'settings.view',
    children: [
      { to: '/settings/aircraft', label: 'Aircraft' },
      { to: '/settings/city-pairs', label: 'City pairs' },
      { to: '/settings/capability', label: 'Capability' },
      { to: '/settings/airports', label: 'Airports' },
      { to: '/settings/groups', label: 'Groups' },
      { to: '/settings/timezones', label: 'Timezones' },
      { to: '/settings/countries', label: 'Countries' },
    ],
  },
]

/**
 * Left navigation.
 *
 * Collapses to an icon rail so the Gantt gets the width back when the board
 * is what matters. Only routes that exist appear here — a rail of greyed-out
 * future sections would be instructions about the roadmap, not navigation.
 */
export function Sidebar({
  collapsed,
  onToggleCollapsed,
  onNavigate,
}: {
  collapsed: boolean
  onToggleCollapsed: () => void
  onNavigate?: () => void
}) {
  const { can } = useAuth()
  const { pathname } = useLocation()

  return (
    <nav
      aria-label="Main"
      className={`flex h-full flex-col border-r border-hairline bg-canvas
                  ${collapsed ? 'w-[72px]' : 'w-60'}`}
    >
      <div className={`flex h-16 shrink-0 items-center ${collapsed ? 'justify-center px-3' : 'px-5'}`}>
        <NavLink to="/" onClick={onNavigate} aria-label="OMC">
          <Wordmark compact={collapsed} />
        </NavLink>
      </div>

      <ul className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {NAV.map((entry) => {
          if (entry.capability && !can(entry.capability)) return null
          const sectionOpen = !collapsed && entry.children && pathname.startsWith(entry.to) && entry.to !== '/'

          return (
            <li key={entry.to}>
              <NavLink
                to={entry.to}
                end={entry.to === '/'}
                onClick={onNavigate}
                title={collapsed ? entry.label : undefined}
                className={({ isActive }) =>
                  [
                    't-nav-link flex items-center gap-3 rounded-[12px] py-2.5 transition-colors',
                    collapsed ? 'justify-center px-2.5' : 'px-3',
                    isActive
                      ? 'bg-surface-strong text-ink'
                      : 'text-body hover:bg-surface-soft active:bg-surface-strong',
                  ].join(' ')
                }
              >
                <span className="shrink-0" aria-hidden="true">
                  {entry.icon}
                </span>
                {!collapsed && <span className="truncate">{entry.label}</span>}
              </NavLink>

              {sectionOpen && (
                <ul className="mt-1 mb-2 ml-[26px] space-y-0.5 border-l border-hairline-soft pl-3">
                  {entry.children?.map((child) => (
                    <li key={child.to}>
                      <NavLink
                        to={child.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          [
                            't-body-sm block rounded-[10px] px-3 py-2 transition-colors',
                            isActive
                              ? 'bg-surface-soft text-ink'
                              : 'text-muted hover:bg-surface-soft hover:text-body',
                          ].join(' ')
                        }
                      >
                        {child.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>

      <div className="hidden shrink-0 border-t border-hairline p-3 lg:block">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          className={`t-nav-link flex w-full items-center gap-3 rounded-[12px] py-2.5 text-body
                      transition-colors hover:bg-surface-soft active:bg-surface-strong
                      ${collapsed ? 'justify-center px-2.5' : 'px-3'}`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
            className={collapsed ? 'rotate-180' : ''}
          >
            <path
              d="M11 4 6 9l5 5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </nav>
  )
}

function DashboardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M2.5 2.5h5v5h-5v-5Zm8 0h5v3h-5v-3Zm0 6h5v7h-5v-7Zm-8 3h5v3.5h-5V11.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function GanttIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M3 4.5h8M5.5 9h9M2.5 13.5h7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M9 1.8v1.6M9 14.6v1.6M16.2 9h-1.6M3.4 9H1.8m11.2-5.1-1.1 1.1M6.5 11.5l-1.1 1.1m0-8.7 1.1 1.1m5.4 5.4 1.1 1.1"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}
